import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import callApi from 'lib/callApi';
import { companyIdAtom } from 'views/state';

import { checkNonZeroArray } from '../../helper';
import { DashboardType, MetricsType, MetricsView } from '../../types';
import { appliedWAIdsAtom, severityFiltersAtom } from '../state';

export const isMetricsSectionOpenAtom = atom(false);
export const metricsViewAtom = atomWithStorage(
  'wa_metrics_view',
  MetricsView.Severity,
);
export const timeViewLabels = [
  '0 to 3 Days',
  '3+ to 7 Days',
  '7+ to 14 Days',
  '14+ to 28 Days',
  '28+ Days',
];

export const vulnMetricsDataAtom = atom(async (get) => {
  const isOpen = get(isMetricsSectionOpenAtom);
  if (isOpen) {
    const companyId = get(companyIdAtom);
    const selectedWAIds = get(appliedWAIdsAtom);

    try {
      if (companyId && companyId !== -1) {
        const params = {
          company_id: companyId,
          host_type: DashboardType.WAS,
        };
        if (selectedWAIds?.length > 0) {
          params['obj_id_list'] = selectedWAIds;
        }

        const responseData = await callApi('dashboard/metrics', 'GET', params);
        return responseData?.data;
      }
    } catch (error) {
      console.error(error);
    }
  }
  return undefined;
});

export const openVulnMetricsBySeverityAverageDayTotalVulnAtom = atom(
  async (get) => {
    const data: any = await get(vulnMetricsDataAtom);
    const severityFilters = get(severityFiltersAtom);
    if (data) {
      return getVulnMetrics(
        data.severityBasedData,
        MetricsType.Open,
        'Days',
        severityFilters,
      );
    }
    return undefined;
  },
);

export const openVulnMetricsBySeverityCountsAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetrics(
      data.severityBasedData,
      MetricsType.Open,
      'Count',
      severityFilters,
    );
  }
  return undefined;
});

export const closeVulnMetricsBySeverityAverageDayTotalVulnAtom = atom(
  async (get) => {
    const data: any = await get(vulnMetricsDataAtom);
    const severityFilters = get(severityFiltersAtom);
    if (data) {
      return getVulnMetrics(
        data.severityBasedData,
        MetricsType.Closed,
        'Days',
        severityFilters,
      );
    }
    return undefined;
  },
);

export const closedVulnMetricsBySeverityCountsAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetrics(
      data.severityBasedData,
      MetricsType.Closed,
      'Count',
      severityFilters,
    );
  }
  return undefined;
});

export const openVulnMetricsByTimeCountsAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetricsByTimeData(
      data.timeBasedData,
      MetricsType.Open,
      severityFilters,
    );
  }
  return undefined;
});

export const closedVulnMetricsByTimeCountsAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetricsByTimeData(
      data.timeBasedData,
      MetricsType.Closed,
      severityFilters,
    );
  }
  return undefined;
});

function getVulnMetrics(
  sourceData: any,
  metricsType: MetricsType,
  propertyName: string,
  severityFilter: any,
) {
  const webappNode = sourceData[metricsType];

  const webappValues: any[] = Object.values(webappNode);
  const webappData = webappValues.map((x) => x[propertyName]);

  const resultsArray: number[][] = [];
  if (checkNonZeroArray(webappData)) resultsArray.push(webappData);

  const averageDaysOpenClose = getVulnMetricsAverageDaysOpenClose(
    sourceData,
    metricsType,
    severityFilter,
  );
  const totalVulnsCount = getVulnMetricsTotalVulnsCount(
    sourceData,
    metricsType,
    severityFilter,
  );
  return {
    metricsData: resultsArray,
    averageDaysOpenClose: averageDaysOpenClose,
    totalVulnsCount: totalVulnsCount,
  };
}

function getVulnMetricsTotalVulnsCount(
  sourceData: any,
  metricsType: MetricsType,
  severityFilters: string[],
) {
  const filteredMetricsBySeverity = filterMetricsBySeverity(
    sourceData,
    metricsType,
    severityFilters,
  );

  const validVulnerabilities = filteredMetricsBySeverity.filter(
    (v) => v.Count > 0,
  );
  const totalCount = validVulnerabilities.reduce((sum, v) => sum + v.Count, 0);
  return totalCount;
}

function getVulnMetricsAverageDaysOpenClose(
  sourceData: any,
  metricsType: MetricsType,
  severityFilters: any,
) {
  let filteredMetricsBySeverity = filterMetricsBySeverity(
    sourceData,
    metricsType,
    severityFilters,
  );

  const validVulnerabilities = filteredMetricsBySeverity.filter(
    (v) => v.Count > 0,
  );
  const totalDays = validVulnerabilities.reduce(
    (sum, v) => sum + v.Days * v.Count,
    0,
  );
  const totalCount = validVulnerabilities.reduce((sum, v) => sum + v.Count, 0);
  const averageDaysOpenClose = totalCount > 0 ? totalDays / totalCount : 0;
  return averageDaysOpenClose.toFixed(1);
}

function filterMetricsBySeverity(
  sourceData: any,
  metricsType: MetricsType,
  severityFilters: any,
) {
  const webAppNode = sourceData[metricsType];

  const severities = ['Urgent', 'Critical', 'Medium', 'Minimal', 'Serious'];

  let resultArray: any[] = [];
  severities.forEach((severity) => {
    if (severityFilters.includes(severity)) {
      const webAppValues = webAppNode[severity];
      resultArray.push(webAppValues);
    }
  });
  return resultArray;
}

function getVulnMetricsByTimeData(
  sourceData: any,
  metricsType: MetricsType,
  severityFilter: any,
) {
  let webappData: number[] = [];

  severityFilter.forEach((severity) => {
    const webappNode = sourceData[metricsType][severity];
    const webappValues: any[] = Object.values(webappNode);

    for (var i = 0; i < webappValues.length; i++) {
      if (webappData[i]) webappData[i] += webappValues[i];
      else webappData[i] = webappValues[i];
    }
  });

  const resultsArray: number[][] = [];
  if (checkNonZeroArray(webappData)) resultsArray.push(webappData);
  return resultsArray;
}
